import React from "react";
import { cn } from "@/lib/utils";
import { Loader2, X } from "lucide-react";

export const Card = ({ className, children, ...rest }) => (
  <div className={cn("bg-white border border-line rounded-xl p-5", className)} {...rest}>
    {children}
  </div>
);

export const Button = ({ variant = "primary", loading, disabled, className, children, ...rest }) => {
  const base = "inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-5 py-2.5 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]";
  const variants = {
    primary: "bg-brand text-white hover:bg-brand-dark",
    accent: "bg-mint text-brand-dark hover:bg-mint-dark hover:text-white",
    secondary: "bg-brand-tint text-brand-dark border border-line hover:bg-white",
    ghost: "bg-transparent text-brand hover:bg-brand-tint",
    danger: "bg-danger text-white hover:opacity-90",
  };
  return (
    <button className={cn(base, variants[variant], className)} disabled={disabled || loading} {...rest}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

export const Field = ({ label, className, hint, ...rest }) => (
  <label className="block mb-4">
    {label && <span className="block text-xs font-semibold text-subtle uppercase tracking-wide mb-1.5">{label}</span>}
    <input
      className={cn(
        "w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-[15px] text-ink outline-none",
        "focus:border-brand focus:ring-2 focus:ring-brand/15 transition-all placeholder:text-subtle/60",
        className
      )}
      {...rest}
    />
    {hint && <span className="block text-xs text-subtle mt-1">{hint}</span>}
  </label>
);

export const TextArea = ({ label, className, rows = 4, ...rest }) => (
  <label className="block mb-4">
    {label && <span className="block text-xs font-semibold text-subtle uppercase tracking-wide mb-1.5">{label}</span>}
    <textarea
      rows={rows}
      className={cn(
        "w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-[15px] text-ink outline-none resize-y",
        "focus:border-brand focus:ring-2 focus:ring-brand/15 transition-all placeholder:text-subtle/60",
        className
      )}
      {...rest}
    />
  </label>
);

export const Label = ({ children, className }) => (
  <span className={cn("text-xs font-semibold text-subtle uppercase tracking-wide", className)}>{children}</span>
);

export const PageHeader = ({ eyebrow, title, action }) => (
  <div className="flex items-end justify-between mb-6">
    <div>
      {eyebrow && <div className="text-xs font-semibold text-brand uppercase tracking-wide">{eyebrow}</div>}
      <h1 className="font-display text-3xl text-ink mt-1">{title}</h1>
    </div>
    {action}
  </div>
);

export const EmptyState = ({ icon: Icon, title, body, action, ...rest }) => (
  <div className="text-center py-8 px-4" {...rest}>
    {Icon && (
      <div className="mx-auto w-12 h-12 rounded-full bg-mint-tint flex items-center justify-center mb-3">
        <Icon className="w-6 h-6 text-mint-dark" />
      </div>
    )}
    <div className="font-semibold text-ink">{title}</div>
    {body && <p className="text-sm text-subtle mt-1 max-w-sm mx-auto">{body}</p>}
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);

export const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <Loader2 className="w-7 h-7 animate-spin text-brand" />
  </div>
);

export const Modal = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-brand/40 backdrop-blur-sm p-0 sm:p-4"
         onClick={onClose} data-testid={testid ? `${testid}-overlay` : undefined}>
      <div
        className="bg-surface w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto kb-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-ink">{title}</h2>
          <button onClick={onClose} className="text-subtle hover:text-ink" data-testid={testid ? `${testid}-close` : undefined}>
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Alert = ({ tone = "error", children, testid }) => {
  if (!children) return null;
  const tones = {
    error: "text-danger",
    success: "text-ok",
    warn: "text-warn",
  };
  return <p className={cn("text-sm mt-3", tones[tone])} data-testid={testid}>{children}</p>;
};
